import React, { useState, useEffect } from 'react';
import dayjs from 'dayjs';
import 'dayjs/locale/es';
import {
  Container,
  Box,
  Typography,
  Button,
  TextField,
  Alert,
  Paper,
  Avatar,
  InputAdornment,
  Stack,
  Chip,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Person as PersonIcon,
  CheckCircle as CheckCircleIcon,
  Login as LoginIcon,
  Logout as LogoutIcon,
  TouchApp as TouchAppIcon,
  AccessTime as AccessTimeIcon,
  ArrowBack as ArrowBackIcon,
  WarningAmber as WarningIcon,
} from '@mui/icons-material';
import { getEstadoHoy, registrarAsistenciaLaboralQR } from '../../services/asistenciaLaboral.service';
import BurbujasLoader from '../../components/UIElements/BurbujasLoader';
import logoCba from '../../components/imagenes/gobierno_blanco.png';

dayjs.locale('es');

export default function RegistroAsistenciaLaboralQR() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [currentTime, setCurrentTime] = useState(dayjs().format('HH:mm:ss'));
  const [currentDate, setCurrentDate] = useState(dayjs().format('dddd D [de] MMMM [de] YYYY'));

  const [cuil, setCuil] = useState('');
  const [participantInfo, setParticipantInfo] = useState(null);
  const [estadoHoy, setEstadoHoy] = useState(null);
  const [tipoRegistro, setTipoRegistro] = useState('ENTRADA');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Resultado
  const [resultData, setResultData] = useState(null);
  const [alreadyRegistered, setAlreadyRegistered] = useState(false);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(dayjs().format('HH:mm:ss'));
      setCurrentDate(dayjs().format('dddd D [de] MMMM [de] YYYY'));
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  const handleSearchCiDi = async () => {
    const clean = cuil.replace(/\D/g, '');
    if (clean.length !== 11) {
      setError('Por favor, ingresá un CUIL válido (11 dígitos sin guiones).');
      return;
    }

    setLoading(true);
    setError('');
    setParticipantInfo(null);
    setEstadoHoy(null);
    setResultData(null);
    setAlreadyRegistered(false);

    try {
      const data = await getEstadoHoy(clean);
      setParticipantInfo({
        cuil: clean,
        nombre: data.nombre,
        apellido: data.apellido,
      });
      setEstadoHoy(data);

      if (data.siguiente_accion === 'SALIDA') {
        setTipoRegistro('SALIDA');
      } else {
        setTipoRegistro('ENTRADA');
      }
    } catch (err) {
      setError(err.message || 'No se pudieron obtener los datos de CiDi para este CUIL.');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmAttendance = async () => {
    if (!participantInfo) return;

    setLoading(true);
    setError('');

    try {
      const res = await registrarAsistenciaLaboralQR({
        cuil: participantInfo.cuil,
        tipo_registro: tipoRegistro,
      });

      if (res.code === 'ALREADY_REGISTERED') {
        setAlreadyRegistered(true);
        setResultData(res.registro);
      } else {
        setResultData(res.registro);
      }
    } catch (err) {
      setError(err.message || 'Ocurrió un error al registrar la asistencia laboral.');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setCuil('');
    setParticipantInfo(null);
    setEstadoHoy(null);
    setTipoRegistro('ENTRADA');
    setResultData(null);
    setAlreadyRegistered(false);
    setError('');
  };


  return (
    <Box
      sx={{
        minHeight: '100vh',
        minHeight: '100dvh',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: 'background.default',
      }}
    >
      <Container
        maxWidth="xs"
        sx={{
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          py: { xs: 2, sm: 4 },
          px: { xs: 2, sm: 3 },
          width: '100%',
        }}
      >
        {/* Reloj institucional en vivo */}
        <Paper
          elevation={0}
          sx={{
            width: '100%',
            p: { xs: 1.25, sm: 1.75 },
            mb: { xs: 2, sm: 2.5 },
            borderRadius: '16px',
            bgcolor: 'common.white',
            border: '1px solid',
            borderColor: 'grey.300',
            textAlign: 'center',
            boxShadow: '0 4px 16px rgba(0,0,0,0.04)',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, color: 'secondary.main' }}>
            <AccessTimeIcon sx={{ fontSize: { xs: 18, sm: 22 } }} />
            <Typography
              variant="h5"
              sx={{
                fontWeight: 'bold',
                letterSpacing: 1.5,
                color: 'secondary.main',
                fontSize: { xs: '1.25rem', sm: '1.5rem' },
              }}
            >
              {currentTime}
            </Typography>
          </Box>
          <Typography
            variant="caption"
            sx={{
              color: 'text.secondary',
              textTransform: 'capitalize',
              display: 'block',
              mt: 0.2,
              fontSize: { xs: '0.72rem', sm: '0.8rem' },
            }}
          >
            {currentDate}
          </Typography>
        </Paper>

        <Paper
          elevation={0}
          sx={{
            borderRadius: '24px',
            width: '100%',
            overflow: 'hidden',
            boxShadow: '0 12px 48px rgba(0,0,0,0.08)',
            border: '1px solid',
            borderColor: 'grey.300',
            bgcolor: 'common.white',
          }}
        >
          <Box sx={{ p: { xs: 2.5, sm: 3.5 }, textAlign: 'center' }}>
            {/* Header del Formulario */}
            <Typography
              variant="h5"
              sx={{
                fontWeight: 'bold',
                color: 'secondary.main',
                mb: 0.5,
                fontSize: { xs: '1.2rem', sm: '1.45rem' },
              }}
            >
              Control de Asistencia Laboral
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: { xs: 2.5, sm: 3 } }}>
              Gobierno de la Provincia de Córdoba
            </Typography>

            {/* PASO 1: Ingreso de CUIL */}
            {!participantInfo && !resultData && (
              <>
                <Avatar
                  sx={{
                    mx: 'auto',
                    mb: 2.5,
                    width: { xs: 64, sm: 76 },
                    height: { xs: 64, sm: 76 },
                    bgcolor: 'grey.100',
                    color: 'primary.main',
                    boxShadow: '0 8px 16px rgba(0, 154, 218, 0.15)',
                  }}
                >
                  <PersonIcon sx={{ fontSize: { xs: 32, sm: 40 } }} />
                </Avatar>

                <Typography
                  variant="h6"
                  sx={{
                    fontFamily: 'Geogrotesque Sharp, sans-serif',
                    fontWeight: 'bold',
                    color: 'secondary.main',
                    mb: 0.5,
                    fontSize: { xs: '1.15rem', sm: '1.3rem' },
                  }}
                >
                  Paso 1 de 2: Identificación del trabajador
                </Typography>

                <Typography
                  variant="body2"
                  sx={{
                    color: 'text.secondary',
                    mb: 2.5,
                    fontSize: { xs: '0.85rem', sm: '0.95rem' },
                  }}
                >
                  Ingresá tu CUIL para registrar tu asistencia laboral
                </Typography>

                <Box sx={{ mb: 2.5 }}>
                  <TextField
                    placeholder="20123456789"
                    variant="outlined"
                    fullWidth
                    autoFocus
                    inputProps={{ inputMode: 'numeric', maxLength: 11 }}
                    value={cuil}
                    onChange={(e) => setCuil(e.target.value.replace(/\D/g, '').slice(0, 11))}
                    disabled={loading}
                    helperText="11 dígitos numéricos sin guiones"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <PersonIcon sx={{ color: 'text.secondary' }} />
                        </InputAdornment>
                      ),
                      sx: {
                        borderRadius: '14px',
                        fontSize: { xs: '1.15rem', sm: '1.3rem' },
                        fontWeight: 600,
                        letterSpacing: 2,
                        bgcolor: 'grey.100',
                        '& fieldset': { borderColor: 'grey.300' },
                        '&:hover fieldset': { borderColor: 'primary.main' },
                      },
                    }}
                    FormHelperTextProps={{ sx: { textAlign: 'center' } }}
                  />
                </Box>

                {error && (
                  <Alert severity="error" sx={{ borderRadius: '12px', mb: 2.5, textAlign: 'left' }}>
                    {error}
                  </Alert>
                )}

                <Button
                  variant="contained"
                  onClick={handleSearchCiDi}
                  disabled={loading || cuil.length !== 11}
                  fullWidth
                  sx={{
                    fontFamily: 'Geogrotesque Sharp, sans-serif',
                    borderRadius: '14px',
                    py: { xs: 1.5, sm: 1.75 },
                    fontWeight: 'bold',
                    fontSize: { xs: '1rem', sm: '1.1rem' },
                    bgcolor: 'secondary.main',
                    boxShadow: '0 4px 14px rgba(0, 69, 130, 0.3)',
                    '&:hover': { bgcolor: 'custom.CBA_Blue_Darker' },
                  }}
                >
                  {loading ? <BurbujasLoader small /> : 'BUSCAR DATOS'}
                </Button>
              </>
            )}

            {/* PASO 2: Confirmación de datos y selección de Entrada/Salida */}
            {participantInfo && !resultData && (
              <>
                {/* Caso A: Jornada ya completada (ya tiene Entrada y Salida hoy) */}
                {estadoHoy?.siguiente_accion === 'COMPLETO' ? (
                  <>
                    <Avatar
                      sx={{
                        mx: 'auto',
                        mb: 2,
                        width: { xs: 64, sm: 72 },
                        height: { xs: 64, sm: 72 },
                        bgcolor: 'rgba(50, 164, 48, 0.12)',
                        color: 'success.main',
                        boxShadow: '0 8px 20px rgba(50, 164, 48, 0.2)',
                      }}
                    >
                      <CheckCircleIcon sx={{ fontSize: { xs: 36, sm: 42 } }} />
                    </Avatar>

                    <Typography
                      variant="h5"
                      sx={{
                        fontWeight: 'bold',
                        color: 'secondary.main',
                        mb: 0.5,
                        fontSize: { xs: '1.2rem', sm: '1.4rem' },
                      }}
                    >
                      Jornada Completa
                    </Typography>

                    <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2.5 }}>
                      Ya registraste tu <strong>Entrada</strong> y tu <strong>Salida</strong> para el día de hoy.
                    </Typography>

                    <Paper
                      elevation={0}
                      sx={{
                        bgcolor: 'grey.100',
                        p: { xs: 2, sm: 2.5 },
                        borderRadius: '16px',
                        mb: 3,
                        border: '1px solid',
                        borderColor: 'grey.300',
                        textAlign: 'left',
                      }}
                    >
                      <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, display: 'block' }}>
                        TRABAJADOR
                      </Typography>
                      <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: 'secondary.main', mb: 1.5 }}>
                        {participantInfo.nombre} {participantInfo.apellido}
                      </Typography>

                      <Stack spacing={1.25} sx={{ pt: 1.5, borderTop: '1px dashed', borderColor: 'grey.400' }}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                            <LoginIcon sx={{ color: 'success.main', fontSize: 18 }} />
                            <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                              Entrada Registrada:
                            </Typography>
                          </Box>
                          <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 700, color: 'success.main' }}>
                            {estadoHoy.entrada?.hora ? estadoHoy.entrada.hora.substring(0, 5) : '--:--'} hs
                          </Typography>
                        </Stack>

                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                            <LogoutIcon sx={{ color: 'warning.main', fontSize: 18 }} />
                            <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                              Salida Registrada:
                            </Typography>
                          </Box>
                          <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 700, color: 'warning.main' }}>
                            {estadoHoy.salida?.hora ? estadoHoy.salida.hora.substring(0, 5) : '--:--'} hs
                          </Typography>
                        </Stack>
                      </Stack>
                    </Paper>

                    <Button
                      variant="contained"
                      onClick={handleReset}
                      fullWidth
                      sx={{
                        borderRadius: '14px',
                        py: { xs: 1.5, sm: 1.75 },
                        fontWeight: 'bold',
                        bgcolor: 'secondary.main',
                        fontSize: { xs: '0.95rem', sm: '1rem' },
                        '&:hover': { bgcolor: 'custom.CBA_Blue_Darker' },
                      }}
                    >
                      REGISTRAR OTRO TRABAJADOR
                    </Button>
                  </>
                ) : (
                  <>
                    {/* Caso B: Flujo Normal (Confirmar Entrada o Salida) */}
                    <Alert
                      severity={tipoRegistro === 'ENTRADA' ? 'warning' : 'warning'}
                      icon={false}
                      sx={{
                        mb: { xs: 1.5, sm: 2 },
                        p: { xs: 1, sm: 1.25 },
                        borderRadius: '14px',
                        textAlign: 'center',
                        fontFamily: 'Poppins, sans-serif',
                        bgcolor: '#FFFBEB',
                        border: '2px solid #FCD34D',
                        color: '#92400E',
                        justifyContent: 'center',
                        alignItems: 'center',
                        '& .MuiAlert-message': {
                          width: '100%',
                          p: 0,
                          textAlign: 'center',
                        },
                      }}
                    >
                      <Typography
                        variant="subtitle2"
                        sx={{
                          fontWeight: 700,
                          fontFamily: 'Poppins, sans-serif',
                          fontSize: { xs: '0.82rem', sm: '0.92rem' },
                        }}
                      >
                        {tipoRegistro === 'ENTRADA'
                          ? '⚠️ ¡PASO FINAL: PRESIONÁ EL BOTÓN VERDE ABAJO PARA CONFIRMAR TU ENTRADA!'
                          : '⚠️ ¡PASO FINAL: PRESIONÁ EL BOTÓN NARANJA ABAJO PARA CONFIRMAR TU SALIDA!'}
                      </Typography>
                    </Alert>

                    <Typography
                      variant="h6"
                      sx={{
                        fontFamily: 'Geogrotesque Sharp, sans-serif',
                        fontWeight: 'bold',
                        color: 'secondary.main',
                        mb: 0.2,
                        fontSize: { xs: '1.15rem', sm: '1.35rem' },
                      }}
                    >
                      {tipoRegistro === 'ENTRADA'
                        ? 'Paso 2 de 2: Confirmación de Entrada'
                        : 'Paso 2 de 2: Confirmación de Salida'}
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{
                        fontFamily: 'Poppins, sans-serif',
                        color: 'text.secondary',
                        mb: { xs: 1.5, sm: 2 },
                        fontSize: { xs: '0.78rem', sm: '0.875rem' },
                      }}
                    >
                      Verificá tus datos antes de presionar el botón de confirmación.
                    </Typography>

                    <Paper
                      elevation={0}
                      sx={{
                        bgcolor: 'grey.100',
                        p: { xs: 1.75, sm: 2.25 },
                        borderRadius: '16px',
                        mb: 2.5,
                        border: '1px solid',
                        borderColor: 'grey.300',
                        textAlign: 'left',
                      }}
                    >
                      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                        <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                          DATOS DEL TRABAJADOR
                        </Typography>
                        <Chip label="CiDi Verificado" size="small" color="success" variant="outlined" sx={{ height: 20, fontSize: '0.68rem', fontWeight: 600 }} />
                      </Stack>

                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 'bold',
                          color: 'secondary.main',
                          lineHeight: 1.2,
                          fontSize: { xs: '1.1rem', sm: '1.25rem' },
                        }}
                      >
                        {participantInfo.nombre} {participantInfo.apellido}
                      </Typography>

                      <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 600, mt: 0.5 }}>
                        CUIL: {participantInfo.cuil}
                      </Typography>

                      {/* Estado previo del día */}
                      {estadoHoy?.entrada && (
                        <Box sx={{ mt: 1.5, pt: 1.25, borderTop: '1px dashed', borderColor: 'grey.300', display: 'flex', alignItems: 'center', gap: 1 }}>
                          <CheckCircleIcon sx={{ fontSize: 16, color: 'success.main' }} />
                          <Typography variant="caption" sx={{ fontWeight: 600, color: 'success.main' }}>
                            Entrada registrada hoy a las {estadoHoy.entrada.hora.substring(0, 5)} hs
                          </Typography>
                        </Box>
                      )}
                    </Paper>

                    {error && (
                      <Alert severity="error" sx={{ borderRadius: '12px', mb: 2.5, textAlign: 'left' }}>
                        {error}
                      </Alert>
                    )}

                    <Stack spacing={1.5}>
                      <Button
                        variant="contained"
                        onClick={handleConfirmAttendance}
                        disabled={loading}
                        fullWidth
                        startIcon={
                          !loading && (
                            <TouchAppIcon
                              sx={{ fontSize: { xs: '22px !important', sm: '26px !important' } }}
                            />
                          )
                        }
                        sx={{
                          fontFamily: 'Geogrotesque Sharp, sans-serif',
                          borderRadius: '14px',
                          py: { xs: 1.5, sm: 1.75 },
                          px: 2,
                          fontWeight: 'bold',
                          fontSize: { xs: '1rem', sm: '1.15rem' },
                          bgcolor: tipoRegistro === 'ENTRADA' ? '#32A430' : '#EE9800',
                          color: '#FFFFFF',
                          letterSpacing: 0.5,
                          animation:
                            tipoRegistro === 'ENTRADA'
                              ? 'pulseGreen 2s infinite ease-in-out'
                              : 'pulseOrange 2s infinite ease-in-out',
                          '@keyframes pulseGreen': {
                            '0%': { transform: 'scale(1)', boxShadow: '0 4px 14px rgba(50, 164, 48, 0.4)' },
                            '50%': { transform: 'scale(1.015)', boxShadow: '0 6px 20px rgba(50, 164, 48, 0.65)' },
                            '100%': { transform: 'scale(1)', boxShadow: '0 4px 14px rgba(50, 164, 48, 0.4)' },
                          },
                          '@keyframes pulseOrange': {
                            '0%': { transform: 'scale(1)', boxShadow: '0 4px 14px rgba(238, 152, 0, 0.4)' },
                            '50%': { transform: 'scale(1.015)', boxShadow: '0 6px 20px rgba(238, 152, 0, 0.65)' },
                            '100%': { transform: 'scale(1)', boxShadow: '0 4px 14px rgba(238, 152, 0, 0.4)' },
                          },
                          '&:hover': {
                            bgcolor: tipoRegistro === 'ENTRADA' ? '#288826' : '#D48800',
                            boxShadow:
                              tipoRegistro === 'ENTRADA'
                                ? '0 8px 24px rgba(40, 136, 38, 0.6)'
                                : '0 8px 24px rgba(212, 136, 0, 0.6)',
                          },
                        }}
                      >
                        {loading ? (
                          <BurbujasLoader small />
                        ) : tipoRegistro === 'ENTRADA' ? (
                          '¡CONFIRMAR MI ENTRADA AHORA!'
                        ) : (
                          '¡CONFIRMAR MI SALIDA AHORA!'
                        )}
                      </Button>

                      <Button
                        variant="text"
                        onClick={handleReset}
                        disabled={loading}
                        fullWidth
                        sx={{
                          fontFamily: 'Geogrotesque Sharp, sans-serif',
                          color: 'text.secondary',
                          fontWeight: 'bold',
                          fontSize: { xs: '0.85rem', sm: '0.95rem' },
                          py: { xs: 0.75, sm: 1.25 },
                        }}
                      >
                        CANCELAR Y VOLVER
                      </Button>
                    </Stack>
                  </>
                )}
              </>
            )}


            {/* PASO 3: Estado de Éxito o Registro Previo */}
            {resultData && (
              <>
                {alreadyRegistered ? (
                  <>
                    <Avatar
                      sx={{
                        mx: 'auto',
                        mb: 2,
                        width: { xs: 68, sm: 76 },
                        height: { xs: 68, sm: 76 },
                        bgcolor: 'rgba(238, 152, 0, 0.12)',
                        color: 'warning.main',
                        boxShadow: '0 8px 20px rgba(238, 152, 0, 0.15)',
                      }}
                    >
                      <WarningIcon sx={{ fontSize: { xs: 38, sm: 44 } }} />
                    </Avatar>

                    <Typography variant="h5" sx={{ fontWeight: 'bold', color: 'warning.main', mb: 1, fontSize: { xs: '1.2rem', sm: '1.45rem' } }}>
                      Movimiento ya registrado
                    </Typography>

                    <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
                      Ya tenías registrada tu <strong>{tipoRegistro}</strong> para el día de hoy.
                    </Typography>
                  </>
                ) : (
                  <>
                    <Avatar
                      sx={{
                        mx: 'auto',
                        mb: 2,
                        width: { xs: 68, sm: 76 },
                        height: { xs: 68, sm: 76 },
                        bgcolor: 'rgba(50, 164, 48, 0.12)',
                        color: 'success.main',
                        boxShadow: '0 8px 20px rgba(50, 164, 48, 0.2)',
                      }}
                    >
                      <CheckCircleIcon sx={{ fontSize: { xs: 38, sm: 44 } }} />
                    </Avatar>

                    <Typography variant="h5" sx={{ fontWeight: 'bold', color: 'success.main', mb: 1, fontSize: { xs: '1.2rem', sm: '1.45rem' } }}>
                      ¡Registro Exitoso!
                    </Typography>

                    <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
                      Tu asistencia laboral ha quedado registrada correctamente en el sistema.
                    </Typography>
                  </>
                )}

                <Paper
                  elevation={0}
                  sx={{
                    p: { xs: 2, sm: 2.5 },
                    borderRadius: '16px',
                    bgcolor: 'grey.100',
                    border: '1px solid',
                    borderColor: 'grey.300',
                    mb: 3,
                    textAlign: 'left',
                  }}
                >
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, display: 'block' }}>
                    TRABAJADOR
                  </Typography>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: 'secondary.main', mb: 1.5, fontSize: { xs: '1rem', sm: '1.15rem' } }}>
                    {resultData.nombre} {resultData.apellido}
                  </Typography>

                  <Stack direction="row" justifyContent="space-between" sx={{ pt: 1, borderTop: '1px dashed', borderColor: 'grey.400' }}>
                    <Box>
                      <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                        MOVIMIENTO
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 700, color: resultData.tipo_registro === 'ENTRADA' ? 'success.main' : 'warning.main' }}>
                        {resultData.tipo_registro}
                      </Typography>
                    </Box>

                    <Box sx={{ textAlign: 'right' }}>
                      <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                        HORARIO OFICIAL
                      </Typography>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 700, color: 'secondary.main' }}>
                        {resultData.hora ? resultData.hora.substring(0, 5) : currentTime.substring(0, 5)} hs
                      </Typography>
                    </Box>
                  </Stack>
                </Paper>

                <Button
                  variant="contained"
                  onClick={handleReset}
                  fullWidth
                  sx={{
                    borderRadius: '14px',
                    py: { xs: 1.5, sm: 1.75 },
                    fontWeight: 'bold',
                    bgcolor: 'secondary.main',
                    fontSize: { xs: '0.95rem', sm: '1rem' },
                    '&:hover': { bgcolor: 'custom.CBA_Blue_Darker' },
                  }}
                >
                  REGISTRAR OTRO TRABAJADOR
                </Button>
              </>
            )}
          </Box>
        </Paper>
      </Container>

      {/* Footer Institucional */}
      <Box component="footer" sx={{ py: { xs: 2, sm: 3 }, textAlign: 'center', mt: 'auto' }}>
        <Container maxWidth="xs">
          <Stack spacing={1} alignItems="center">
            <Box
              component="img"
              src={logoCba}
              alt="Gobierno de Córdoba"
              sx={{
                width: { xs: '150px', sm: '180px' },
                height: 'auto',
                filter: 'brightness(0) opacity(0.6)',
                mb: 0.5,
              }}
            />
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              Subsecretaría de Formación y Capacitación
            </Typography>
          </Stack>
        </Container>
      </Box>
    </Box>
  );
}
